import {useId, useRef, useState} from 'react'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {Button} from '@primer/react'
import {SponsorHeartIcon} from './components/SponsorHeartIcon'
import {SponsorDialog} from './components/SponsorDialog'
import type {SponsorableUser, FundingLink, SponsorButtonProps} from './types'

const EMPTY_SPONSORABLES: SponsorableUser[] = []
const EMPTY_EXTERNAL_LINKS: FundingLink[] = []

/** Sponsor button with direct-link or modal behavior. */
export function SponsorButton({
  show,
  repoNameWithOwner,
  sponsorables = EMPTY_SPONSORABLES,
  externalFundingLinks = EMPTY_EXTERNAL_LINKS,
  defaultOpen = false,
}: SponsorButtonProps) {
  const hasData = sponsorables.length > 0 || externalFundingLinks.length > 0
  const isSingleSponsor = sponsorables.length === 1 && externalFundingLinks.length === 0
  const canAutoOpen = defaultOpen && hasData && !isSingleSponsor

  const [userOpenedDialog, setUserOpenedDialog] = useState(false)
  // Once the user dismisses the auto-opened dialog, stop auto-opening.
  const [autoOpenDismissed, setAutoOpenDismissed] = useState(false)
  const isDialogOpen = userOpenedDialog || (canAutoOpen && !autoOpenDismissed)

  const buttonRef = useRef<HTMLButtonElement>(null)
  const githubSectionTitleId = useId()
  const externalSectionTitleId = useId()
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  if (!show) {
    return null
  }

  const isSponsoring = sponsorables.some(s => s.isSponsoredByViewer)
  const viewerRepresentsSponsorable = sponsorables.some(s => s.isViewerSponsorable)
  const directSponsor = isSingleSponsor ? sponsorables[0] : undefined
  const buttonText = isSponsoring ? 'Sponsoring' : 'Sponsor'
  const buttonAriaLabel = `${buttonText} ${repoNameWithOwner}`

  const sendSponsorAnalytics = (action: string) =>
    sendClickAnalyticsEvent({
      category: 'REPOSITORY_OVERVIEW',
      action,
      label: `location:header;ref_cta:sponsor_button;is_sponsoring:${isSponsoring}`,
    })

  // Single sponsorable, no external links — render as direct link
  if (directSponsor) {
    const directButtonText = directSponsor.isSponsoredByViewer ? 'Sponsoring' : 'Sponsor'

    return (
      <Button
        as="a"
        href={`/sponsors/${directSponsor.login}`}
        size="small"
        leadingVisual={() => (
          <SponsorHeartIcon
            isSponsoring={directSponsor.isSponsoredByViewer}
            suppressAnimation={viewerRepresentsSponsorable}
          />
        )}
        data-testid="sponsor-button"
        aria-label={`${directButtonText} ${repoNameWithOwner}`}
        onClick={() => sendSponsorAnalytics('click:sponsor_direct')}
      >
        {directButtonText}
      </Button>
    )
  }

  // No data yet — render disabled placeholder
  if (!hasData) {
    return (
      <Button
        size="small"
        leadingVisual={() => (
          <SponsorHeartIcon isSponsoring={isSponsoring} suppressAnimation={viewerRepresentsSponsorable} />
        )}
        aria-disabled="true"
        data-testid="sponsor-button"
        aria-label={buttonAriaLabel}
      >
        {buttonText}
      </Button>
    )
  }

  // Multiple sponsorables or external links — render button + dialog
  return (
    <>
      <Button
        ref={buttonRef}
        size="small"
        leadingVisual={() => (
          <SponsorHeartIcon isSponsoring={isSponsoring} suppressAnimation={viewerRepresentsSponsorable} />
        )}
        data-testid="sponsor-button"
        aria-label={buttonAriaLabel}
        aria-haspopup="dialog"
        aria-expanded={isDialogOpen}
        onClick={() => {
          sendSponsorAnalytics('click:sponsor_dialog_open')
          setUserOpenedDialog(true)
        }}
      >
        {buttonText}
      </Button>
      {isDialogOpen && (
        <SponsorDialog
          repoNameWithOwner={repoNameWithOwner}
          sponsorables={sponsorables}
          externalFundingLinks={externalFundingLinks}
          onClose={() => {
            setUserOpenedDialog(false)
            setAutoOpenDismissed(true)
          }}
          returnFocusRef={buttonRef}
          githubSectionTitleId={githubSectionTitleId}
          externalSectionTitleId={externalSectionTitleId}
        />
      )}
    </>
  )
}
